<template>
  <div class="p-6">
    <h2 class="text-2xl font-bold mb-4">Contact Information</h2>

    <!-- Loading -->
    <div v-if="loading" class="text-gray-500 text-center">
      Loading contact information...
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-red-500 text-center">
      {{ error }}
    </div>

    <!-- No Data -->
    <div v-else-if="contacts.length === 0" class="text-gray-500 text-center">
      No contact data found.
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
        <thead>
          <tr class="bg-gray-100 text-gray-700">
            <th class="px-4 py-2 text-left text-sm font-medium">ID</th>
            <th class="px-4 py-2 text-left text-sm font-medium">Name</th>
            <th class="px-4 py-2 text-left text-sm font-medium">Email</th>
            <th class="px-4 py-2 text-left text-sm font-medium">Message</th>
            <th class="px-4 py-2 text-left text-sm font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="contact in contacts"
            :key="contact.id"
            class="border-t border-gray-200 hover:bg-gray-50"
          >
            <td class="px-4 py-2 text-sm text-gray-600">{{ contact.id }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ contact.name || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ contact.email || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ truncateMessage(contact.message) || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm">
              <button
                @click="deleteContact(contact.id)"
                class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-xs"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'

const contacts = ref([])
const loading = ref(false)
const error = ref(null)

// Fetch contacts
const fetchContacts = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/contacts`)
    if (!response.ok) throw new Error('Failed to fetch contacts')

    const data = await response.json()
    contacts.value = data
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

// Delete contact
const deleteContact = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Contact?',
    text: "Are you sure you want to delete this contact?",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/contacts/${id}`, {
      method: 'DELETE'
    })
    if (!response.ok) throw new Error('Failed to delete contact')

    contacts.value = contacts.value.filter(c => c.id !== id)
    
    Swal.fire({
      icon: 'success',
      title: 'Deleted!',
      text: 'Contact deleted successfully!',
      confirmButtonColor: '#BF3131'
    });
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message,
      confirmButtonColor: '#BF3131'
    });
  }
}

// Helper to limit message length
const truncateMessage = (msg) => {
  if (!msg) return ''
  return msg.length > 100 ? msg.substring(0, 100) + '...' : msg
}

onMounted(fetchContacts)
</script>
